import { getDefinition, type DefLookup } from "./catalog";
import { buildDcCircuitTopology, dcInputPorts, type DcCircuitRoute } from "./dc-circuit-topology";
import { splitV, type Vertex } from "./diagnostics/graph";
import { measure } from "./perf-marks";
import type { System } from "./types";

/** Cranking protection policy is applied after physical circuit resolution.
 * Bank ties remain conducting paths even when they retain ordinary protection. */
export type CrankingCircuit = ReadonlySet<string>;

const cache = new WeakMap<System, Map<DefLookup, CrankingCircuit>>();
export type CrankingPath = {
  loadVertex: Vertex;
  pos: string[];
  neg: string[];
  complete: boolean;
  sourceId?: string;
  /** Physical links inside the source string, separate from external bank ties. */
  seriesJumpers?: string[];
  /** Parallel bank ties carry starter current, but retain their ordinary
   *  overcurrent protection instead of inheriting the starter exemption. */
  bankInterconnects?: string[];
};
const pathCache = new WeakMap<System, Map<DefLookup, CrankingPath[]>>();

/** Complete starter loops, retaining each selectable start battery's route. */
export function collectCrankingCircuits(system: System, defLookup: DefLookup = getDefinition): CrankingPath[] {
  const cached = pathCache.get(system)?.get(defLookup);
  if (cached) return cached;
  const paths = collectCrankingCircuitsImpl(system, defLookup);
  const byLookup = pathCache.get(system) ?? new Map();
  byLookup.set(defLookup, paths);
  pathCache.set(system, byLookup);
  return paths;
}

/** Memoized on the System reference: a system mutated in place reads stale. */
export function computeCrankingCables(
  system: System,
  defLookup: DefLookup = getDefinition,
): CrankingCircuit {
  const cached = cache.get(system)?.get(defLookup);
  if (cached) return cached;
  const result = measure("computeCrankingCables", () =>
    new Set(collectCrankingCircuits(system, defLookup).flatMap(c => {
      const bankInterconnects = new Set(c.bankInterconnects);
      return [...c.pos, ...c.neg].filter(id => !bankInterconnects.has(id));
    })),
  );
  const byLookup = cache.get(system) ?? new Map();
  byLookup.set(defLookup, result);
  cache.set(system, byLookup);
  return result;
}

const startingEvidenceCache = new WeakMap<System, Map<DefLookup, ReadonlySet<string>>>();

/** Positive declared starting current on every represented starter route sharing
 * a conductor. This is only evidence for the existing optional fuse heuristic;
 * it neither grants a protection exemption nor certifies a device/curve.
 * Static declarations also apply when simulation is off or a return is missing. */
export function computeKnownStartingCurrentCables(
  system: System,
  defLookup: DefLookup = getDefinition,
): ReadonlySet<string> {
  const cached = startingEvidenceCache.get(system)?.get(defLookup);
  if (cached) return cached;
  const nodes = new Map(system.nodes.map(node => [node.id, node]));
  const evidence = new Map<string, boolean>();
  for (const route of collectCrankingCircuits(system, defLookup)) {
    const node = nodes.get(splitV(route.loadVertex).nodeId);
    const def = node?.kind === "component" ? defLookup(node.definitionId, node.customDefinition) : undefined;
    const currents = [def?.ampDraw, def?.ampSurge];
    const known = currents.some(amps => typeof amps === "number" && Number.isFinite(amps) && amps > 0)
      && currents.every(amps => amps == null || (Number.isFinite(amps) && amps >= 0));
    const interconnects = new Set(route.bankInterconnects);
    for (const id of [...route.pos, ...route.neg]) {
      if (interconnects.has(id)) continue;
      // A second unknown starter must not borrow the first one's rating.
      evidence.set(id, (evidence.get(id) ?? true) && known);
    }
  }
  const result = new Set([...evidence].filter(([, known]) => known).map(([id]) => id));
  const byLookup = startingEvidenceCache.get(system) ?? new Map();
  byLookup.set(defLookup, result);
  startingEvidenceCache.set(system, byLookup);
  return result;
}

/** Locate a bank boundary on a physical route without changing reachability. */
function bankInterconnects(route: DcCircuitRoute, posts: ReadonlySet<Vertex>): string[] {
  let lastPositivePost = -1;
  route.positiveVertices.forEach((vertex, i) => { if (posts.has(vertex)) lastPositivePost = i; });
  const firstNegativePost = route.negativeVertices.findIndex(vertex => posts.has(vertex));
  return [
    ...route.positive.filter(step => route.positiveVertices.indexOf(step.from) < lastPositivePost),
    ...route.negative.filter(step => firstNegativePost >= 0 && route.negativeVertices.indexOf(step.from) >= firstNegativePost),
  ].map(step => step.cableId);
}

function collectCrankingCircuitsImpl(system: System, defLookup: DefLookup): CrankingPath[] {
  if (!system.cables.length) return [];
  const topology = buildDcCircuitTopology(system, defLookup);
  const nodes = new Map(system.nodes.map(node => [node.id, node]));
  const out: CrankingPath[] = [];
  for (const port of dcInputPorts(system, defLookup)) {
    const node = nodes.get(port.nodeId);
    if (node?.kind !== "component" || defLookup(node.definitionId, node.customDefinition)?.category !== "engine") continue;
    const resolution = topology.resolvePort(port);
    const routes = resolution.routes.filter(route => route.source.kind === "battery");
    for (const route of routes) {
      const seriesJumpers = [...route.series].reverse().map(step => step.cableId);
      const interconnects = bankInterconnects(route, topology.batteryPosts);
      out.push({
        loadVertex: route.positiveTerminal,
        pos: [...route.positive].reverse().map(step => step.cableId).concat(seriesJumpers),
        neg: route.negative.map(step => step.cableId),
        complete: true, sourceId: route.sourceId,
        ...(seriesJumpers.length ? { seriesJumpers } : {}),
        ...(interconnects.length ? { bankInterconnects: interconnects } : {}),
      });
    }
    if (routes.length) continue;
    // Preserve the design-time positive-only estimate separately. An omitted
    // return is never recorded as a complete physical circuit or source pair.
    for (const route of resolution.incomplete) {
      if (route.source.kind !== "battery" || route.source.levels.length !== 1 || route.reason === "shorted-rails") continue;
      let lastPost = -1;
      route.positiveVertices.forEach((vertex, i) => { if (topology.batteryPosts.has(vertex)) lastPost = i; });
      const interconnects = route.positive.filter(step => route.positiveVertices.indexOf(step.from) < lastPost).map(step => step.cableId);
      out.push({
        loadVertex: route.positiveTerminal,
        pos: [...route.positive].reverse().map(step => step.cableId), neg: [],
        complete: false, sourceId: route.sourceId,
        ...(interconnects.length ? { bankInterconnects: interconnects } : {}),
      });
    }
  }
  return out;
}
